import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CodyIcon } from '../components/CodyLogo';
import { Users, Crown, Globe, Search, ChevronRight, LogOut, Megaphone, DollarSign, TrendingUp, BarChart3, UserPlus, CreditCard, Tag } from 'lucide-react';

const PLAN_COLORS = { free: { bg: '#F6F7F9', fg: '#5B667A', label: 'Startup' }, pro: { bg: '#FFF2EA', fg: '#FF4500', label: 'Growth' }, agency: { bg: '#EFF6FF', fg: '#2563EB', label: 'Agency' } };
function PlanBadge({ plan }) { const p = PLAN_COLORS[plan] || PLAN_COLORS.free; return <Badge className="text-[10px] font-semibold" style={{ background: p.bg, color: p.fg }}>{p.label}</Badge>; }
function StatusDot({ suspended, plan }) {
  if (suspended) return <span className="text-[10px] font-medium text-red-500">Suspended</span>;
  if (plan && plan !== 'free') return <span className="flex items-center gap-1.5 justify-center"><span className="relative flex h-2 w-2"><span className="animate-ping absolute h-full w-full rounded-full bg-[#16A34A] opacity-75"></span><span className="relative rounded-full h-2 w-2 bg-[#16A34A]"></span></span><span className="text-[10px] font-medium text-[#16A34A]">Paid</span></span>;
  return <span className="text-[10px] font-medium text-[#9ca3af]">Free</span>;
}
function fmt(n) { return typeof n === 'number' ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0.00'; }

export default function Ops() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [annText, setAnnText] = useState('');
  const [annEnabled, setAnnEnabled] = useState(false);
  const [annLink, setAnnLink] = useState('');
  const [annSaving, setAnnSaving] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/dashboard'); return; }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [s, u] = await Promise.all([api.getAdminStats(), api.getAdminUsers()]);
      setStats(s); setUsers(u);
      const ann = await api.getAnnouncement();
      setAnnText(ann.text || ''); setAnnEnabled(ann.enabled || false); setAnnLink(ann.link || '');
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const handleSaveAnnouncement = async () => {
    setAnnSaving(true);
    try { await api.updateAnnouncement({ text: annText, enabled: annEnabled, link: annLink }); toast.success('Announcement updated'); }
    catch (e) { toast.error(e.message); }
    finally { setAnnSaving(false); }
  };

  const filtered = users.filter(u => {
    const matchSearch = !search || u.email.toLowerCase().includes(search.toLowerCase());
    const matchPlan = planFilter === 'all' || (u.plan || 'free') === planFilter;
    return matchSearch && matchPlan;
  });

  if (user?.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="bg-white border-b border-[#E6E8EE] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CodyIcon size={32} />
            <span className="font-bold text-[15px]" style={{ fontFamily: "'Space Grotesk'" }}>Ops Panel</span>
            <span className="text-[10px] text-[#9ca3af] bg-[#F6F7F9] px-2 py-0.5 rounded-full">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" className="text-xs text-[#5B667A]" onClick={() => navigate('/ops/coupons')}>
              <Tag className="w-3.5 h-3.5 mr-1" /> Coupons
            </Button>
            <Button variant="ghost" size="sm" className="text-xs text-[#5B667A]" onClick={() => { localStorage.removeItem('token'); window.location.href = '/ops/login'; }}>
              <LogOut className="w-3.5 h-3.5 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* ── Revenue Stats ── */}
        {stats && (
          <>
            {/* Top row: MRR, ARR, Revenue, Conversion */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              {[
                { l: 'MRR', v: fmt(stats.mrr), icon: DollarSign, color: '#16A34A', sub: 'Monthly recurring' },
                { l: 'ARR', v: fmt(stats.arr), icon: TrendingUp, color: '#2563EB', sub: 'Annual recurring' },
                { l: 'Total Revenue', v: fmt(stats.revenue_total), icon: CreditCard, color: '#FF4500', sub: `${stats.recent_transactions?.length || 0} transactions` },
                { l: 'Conversion', v: `${stats.conversion_rate}%`, icon: BarChart3, color: '#D97706', sub: `${stats.paid_users} paid of ${stats.users}` },
              ].map((s, i) => (
                <Card key={i} className="p-4 rounded-xl border-[#E6E8EE] bg-white">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-semibold text-[#5B667A] uppercase tracking-wider">{s.l}</span>
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${s.color}12` }}>
                      <s.icon className="w-3.5 h-3.5" style={{ color: s.color }} />
                    </div>
                  </div>
                  <p className="text-xl font-extrabold tabular-nums" style={{ fontFamily: "'Space Grotesk'" }}>{s.v}</p>
                  <p className="text-[10px] text-[#9ca3af]">{s.sub}</p>
                </Card>
              ))}
            </div>

            {/* Second row: Revenue by period + Signups + Plan breakdown */}
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
              {[
                { l: 'Revenue Today', v: fmt(stats.revenue_today) },
                { l: 'Revenue This Week', v: fmt(stats.revenue_week) },
                { l: 'Revenue This Month', v: fmt(stats.revenue_month) },
                { l: 'Signups Today', v: stats.signups_today },
                { l: 'Signups This Week', v: stats.signups_week },
                { l: 'Signups This Month', v: stats.signups_month },
              ].map((s, i) => (
                <Card key={i} className="p-3 rounded-xl border-[#E6E8EE] bg-white text-center">
                  <p className="text-lg font-bold tabular-nums" style={{ fontFamily: "'Space Grotesk'" }}>{s.v}</p>
                  <p className="text-[9px] text-[#5B667A]">{s.l}</p>
                </Card>
              ))}
            </div>

            {/* Plan distribution + recent transactions */}
            <div className="grid lg:grid-cols-[1fr_1fr] gap-4 mb-6">
              <Card className="p-4 rounded-xl border-[#E6E8EE] bg-white">
                <h3 className="text-xs font-bold mb-3" style={{ fontFamily: "'Space Grotesk'" }}>Plan Distribution</h3>
                <div className="space-y-2">
                  {[
                    { label: 'Startup (Free)', count: stats.free_users, color: '#5B667A', pct: stats.users > 0 ? Math.round(stats.free_users / stats.users * 100) : 0 },
                    { label: 'Growth', count: stats.pro_users, color: '#FF4500', pct: stats.users > 0 ? Math.round(stats.pro_users / stats.users * 100) : 0 },
                    { label: 'Agency', count: stats.agency_users, color: '#2563EB', pct: stats.users > 0 ? Math.round(stats.agency_users / stats.users * 100) : 0 },
                  ].map((p, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xs text-[#5B667A] w-24">{p.label}</span>
                      <div className="flex-1 h-2 bg-[#F3F4F6] rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${p.pct}%`, background: p.color }} />
                      </div>
                      <span className="text-xs font-bold tabular-nums w-8 text-right">{p.count}</span>
                    </div>
                  ))}
                </div>
              </Card>
              <Card className="p-4 rounded-xl border-[#E6E8EE] bg-white">
                <h3 className="text-xs font-bold mb-3" style={{ fontFamily: "'Space Grotesk'" }}>Recent Transactions</h3>
                {stats.recent_transactions?.length > 0 ? (
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {stats.recent_transactions.map((t, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <div>
                          <span className="font-medium text-[#0B0F17]">{t.email}</span>
                          <span className="text-[#9ca3af] ml-2 capitalize">{t.plan} {t.billing_cycle}</span>
                        </div>
                        <span className="font-bold text-[#16A34A] tabular-nums">${t.amount}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-[#9ca3af]">No transactions yet</p>
                )}
              </Card>
            </div>
          </>
        )}

        {/* Announcement */}
        <Card className="p-5 rounded-xl border-[#E6E8EE] bg-white mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FF6A2A, #FF4500)' }}><Megaphone className="w-4 h-4 text-white" /></div>
              <h3 className="text-sm font-bold" style={{ fontFamily: "'Space Grotesk'" }}>Announcement Bar</h3>
            </div>
            <div className="flex items-center gap-2"><span className="text-xs text-[#5B667A]">{annEnabled ? 'Live' : 'Off'}</span><Switch checked={annEnabled} onCheckedChange={setAnnEnabled} /></div>
          </div>
          <div className="flex gap-3">
            <Input placeholder="Announcement text..." value={annText} onChange={(e) => setAnnText(e.target.value)} className="h-9 text-sm flex-1" />
            <Input placeholder="Link (optional)" value={annLink} onChange={(e) => setAnnLink(e.target.value)} className="h-9 text-sm w-48" />
            <Button size="sm" className="h-9 px-4 text-xs font-semibold rounded-lg" style={{ background: 'linear-gradient(135deg, #FF6A2A, #FF4500)' }} onClick={handleSaveAnnouncement} disabled={annSaving}>{annSaving ? 'Saving...' : 'Save'}</Button>
          </div>
        </Card>

        {/* Users */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-sm"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9ca3af]" /><Input placeholder="Search by email..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 h-9 rounded-lg text-sm bg-white" /></div>
          <Select value={planFilter} onValueChange={setPlanFilter}><SelectTrigger className="w-[140px] h-9 text-sm bg-white"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Plans</SelectItem><SelectItem value="free">Startup</SelectItem><SelectItem value="pro">Growth</SelectItem><SelectItem value="agency">Agency</SelectItem></SelectContent></Select>
          <span className="text-xs text-[#9ca3af]">{filtered.length} users</span>
        </div>

        <Card className="rounded-2xl border-[#E6E8EE] bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#F9FAFB] border-b border-[#E6E8EE]">
                  <th className="text-left text-[10px] font-semibold text-[#5B667A] uppercase tracking-wider px-5 py-3">User</th>
                  <th className="text-center text-[10px] font-semibold text-[#5B667A] uppercase tracking-wider px-3 py-3">Plan</th>
                  <th className="text-center text-[10px] font-semibold text-[#5B667A] uppercase tracking-wider px-3 py-3">Billing</th>
                  <th className="text-center text-[10px] font-semibold text-[#5B667A] uppercase tracking-wider px-3 py-3">Brands</th>
                  <th className="text-center text-[10px] font-semibold text-[#5B667A] uppercase tracking-wider px-3 py-3">Leads</th>
                  <th className="text-center text-[10px] font-semibold text-[#5B667A] uppercase tracking-wider px-3 py-3">AI Used</th>
                  <th className="text-center text-[10px] font-semibold text-[#5B667A] uppercase tracking-wider px-3 py-3">Status</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? Array.from({ length: 5 }).map((_, i) => <tr key={i}><td colSpan={8} className="px-5 py-3"><Skeleton className="h-5 w-full" /></td></tr>) :
                  filtered.map((u) => (
                    <tr key={u.id} className="border-b border-[#E6E8EE]/60 hover:bg-[#FAFBFC] cursor-pointer transition-colors" onClick={() => navigate(`/ops/users/${u.id}`)}>
                      <td className="px-5 py-3"><p className="text-sm font-medium text-[#0B0F17]">{u.email}</p><p className="text-[10px] text-[#9ca3af]">Joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</p></td>
                      <td className="px-3 py-3 text-center"><PlanBadge plan={u.plan || 'free'} /></td>
                      <td className="px-3 py-3 text-center"><span className="text-[10px] text-[#5B667A] capitalize">{(u.plan || 'free') === 'free' ? 'Free' : (u.billing_cycle || 'monthly')}</span></td>
                      <td className="px-3 py-3 text-center"><span className="text-xs font-semibold tabular-nums">{u.businesses_count}</span></td>
                      <td className="px-3 py-3 text-center"><span className="text-xs tabular-nums">{u.leads_count}</span></td>
                      <td className="px-3 py-3 text-center"><span className="text-xs tabular-nums">{u.ai_replies_this_month}</span></td>
                      <td className="px-3 py-3 text-center"><StatusDot suspended={u.is_suspended} plan={u.plan} /></td>
                      <td className="px-2"><ChevronRight className="w-4 h-4 text-[#d1d5db]" /></td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
